import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListViewSettings from "../../components/ListViewSettings";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { displayError } from "../../helpers";
import moment from 'moment';
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatRecordStatus, splitRecordsByStatus, sumField, formatQty, formatAmount, buildDcSummaryRows, isSameListDate } from "../../utils/listSummaryUtils";
import { getHTMLForTablePopup } from "../../utils/PopupUtils";
import { getAllDeliveryChallans, deleteDeliveryChallan ,printBatchReport,printDeliveryChallan,getDcListCSV } from "../../services/DeliveryChallanServices";
import { printSalesInvoice } from "../../services/SalesInvoiceServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildDeliveryChallanQueryParams, DEFAULT_LIST_VIEW_CONFIG } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { getDeliveryChallanDocumentLabel } from './deliveryChallanDocumentTypes';
import { exportAsExcel } from "../../utils/ExcelExporter";

function DeliveryChallanTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
      };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate(); 
    const [refreshKey, setRefreshKey] = useState(0);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);  
    const [listViewConfig, setListViewConfig] = useState(null);
    const left_pinned = ["delivery_challan_no","delivery_challan_date"]
    const right_pinned = ["id"]

    const todayDateLabel = moment().format('DD-MM-YYYY');

    const todayRecords = useMemo(
        () => data.records.filter((row) => isSameListDate(row.delivery_challan_date, todayDateLabel)),
        [data.records, todayDateLabel],
    );

    const todayActiveRecords = useMemo(
        () => todayRecords.filter((row) => !row.is_inactive),
        [todayRecords],
    );

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(
        () => buildGradientCard('Today DC', formatQty(sumField(todayActiveRecords, 'dc_qty')), 'today'),
        [todayActiveRecords],
    );

    const dcSummaryRows = useMemo(
        () => buildDcSummaryRows(activeRecords),
        [activeRecords],
    );

    const todayDcSummaryRows = useMemo(
        () => buildDcSummaryRows(todayActiveRecords),
        [todayActiveRecords],
    );

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total', data.records.length, 'blue'),
            buildGradientCard('Production', formatQty(sumField(activeRecords, 'production_qty')), 'purple'),
            buildGradientCard('DC Qty', formatQty(sumField(activeRecords, 'dc_qty')), 'teal'),
            buildGradientCard('Invoice Qty', formatQty(sumField(activeRecords, 'invoice_qty')), 'blue'),
        ];
        const cancelledQty = sumField(cancelledRecords, 'dc_qty');
        if (cancelledRecords.length && cancelledQty > 0) {
            cards.push(buildGradientCard('Cancelled DC Qty', formatQty(cancelledQty), 'red'));
        }
        return cards;
    }, [data.records.length, activeRecords, cancelledRecords]);

    const mapDeliveryChallanRows = (deliveryChallanList) =>
        (deliveryChallanList || []).map((dc) => {
            const salesOrder = dc.work_schedule?.sales_order_detail?.sales_order;
            const companyBatchFlag = dc.is_batch_report_needed ?? salesOrder?.company?.is_batch_report_needed;
            const isActive = dc.status === true || dc.status === 1;
            const batchReportNeeded =
                companyBatchFlag === 'Yes' ||
                companyBatchFlag === true ||
                companyBatchFlag === 1;
            return {
                plant: dc.work_schedule?.plant?.alias_name ?? '',
                delivery_challan_no: dc.delivery_challan_no,
                delivery_challan_date: dc.delivery_challan_date,
                delivery_challan_time: dc.delivery_challan_time,
                batch_no: dc.batch_no,
                order_no: salesOrder?.order_no ?? '',
                workschedule_no: dc.work_schedule?.schedule_no ?? '',
                customer_name: salesOrder?.consignee?.customer?.name ?? '',
                sales_rep_name: salesOrder?.consignee?.customer?.salesrep?.name ?? '',
                consignee_projectname: salesOrder?.consignee?.project_name ?? '',
                production_plant: dc.production_plant?.alias_name
                    || dc.production_plant?.plant_alias
                    || dc.production_plant?.name
                    || '',
                production_qty: dc.production_qty,
                purchase_qty: dc.purchase_qty,
                other_qty: dc.other_qty,
                received_qty: dc.received_qty,
                client_adjust_qty: dc.client_adjust_qty,
                dc_qty: dc.dc_qty,
                invoice_qty: dc.invoice?.invoice_qty,
                customer_vehicle_no: dc.vehicle?.name ? dc.vehicle.name : dc.customer_vehicle_no,
                delivery_mode: String(dc.work_schedule?.delivery_mode ?? '').toUpperCase(),
                document_type: getDeliveryChallanDocumentLabel(dc.document_type).toUpperCase(),
                eway_bill_no: dc.eway_bill_no || (dc.eway_bill_status === -1 ? 'Generation Failed' : ''),
                einvoice_irn: dc.einvoice_irn || '',
                einvoice_ack: dc.einvoice_ack || '',
                user_remarks: dc.user_remarks ?? '',
                grade: dc.work_schedule?.sales_order_detail?.product?.name ?? '',
                rate: dc.work_schedule?.sales_order_detail?.rate ?? 0,
                invoice_id: dc.invoice?.id ?? null,
                dc_invioce: !isActive
                    ? "Cancelled"
                    : dc.invoice?.invioce_no
                        ? [dc.invoice.prefix, dc.invoice.invioce_no].filter(Boolean).join('/')
                        : "Pending",
                status: formatRecordStatus(isActive),
                is_inactive: !isActive,
                id: dc.id,
                is_batch_report_needed: batchReportNeeded ? "Yes" : "No",
                hide_row_edit: dc.work_schedule?.is_schedule_completed ? "Yes" : "No",
                hide_row_print: dc.hide_row_print ?? (isActive ? "No" : "Yes"),
            };
        });

    const loadDeliveryChallanList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        const activeConfig = config || DEFAULT_LIST_VIEW_CONFIG;
        setIsLoading(true);
        getAllDeliveryChallans(cookies, buildDeliveryChallanQueryParams(activeConfig))
        .then((deliverychallanList) => {
            setData({
                records: mapDeliveryChallanRows(deliverychallanList?.delivery_challan_list),
            });
        })
        .catch((error) => {
            console.log(error.response?.data ?? error.message);
            setData({ records: [] });
            const errorData = error.response?.data ?? error.message;
            let errorMessage = errorData;
            if (typeof errorData === 'object' && errorData !== null) {
                errorMessage = errorData.detail ?? errorData.message ?? 'Unexpected error occurred.';
            }
            displayError(errorMessage, 'Failed to load delivery challan list');
        })
        .finally(() => {
            setIsLoading(false);
        });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        setListViewConfig(config);
        loadDeliveryChallanList(config);
    }, [loadDeliveryChallanList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return;
        }
        getListViewSettings(cookies, 'delivery_challan')
            .then((response) => {
                const config = response.config || getDefaultListViewConfig('delivery_challan');
                setListViewConfig(config);
                loadDeliveryChallanList(config);
            })
            .catch(() => {
                const config = getDefaultListViewConfig('delivery_challan');
                setListViewConfig(config);
                loadDeliveryChallanList(config);
            });
    }, [cookies, cookies.myToken, loadDeliveryChallanList, refreshKey]);

    const handleTodayDcSummaryClick = () => {
        showDcSummaryPopup(
            `Today's DC Summary (${todayDateLabel})`,
            todayDcSummaryRows,
            `No delivery challans found for today (${todayDateLabel}).`,
        );
    };

    const handleSummaryCardClick = (card) => {
        if (card.label === 'DC Qty') {
            showDcSummaryPopup('DC Qty Summary', dcSummaryRows, 'No delivery challan records found in the current list.');
        }
    };

    const showDcSummaryPopup = (title, summaryRows, emptyMessage) => {
        const tableRows = summaryRows.map((row) => ({
            customer: row.customer,
            site: row.site,
            grade: row.grade,
            dcQty: formatQty(row.dcQty),
            dcCount: row.dcCount,
            rate: formatAmount(row.rate),
            amount: formatAmount(row.amount),
        }));

        const totalDcQty = sumField(summaryRows, 'dcQty');
        const totalDcCount = summaryRows.reduce((sum, row) => sum + row.dcCount, 0);
        const totalAmount = summaryRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

        Swal.fire({
            title,
            html: getHTMLForTablePopup(
                title,
                [
                    { key: 'customer', label: 'Customer' },
                    { key: 'site', label: 'Site' },
                    { key: 'grade', label: 'Grade' },
                    { key: 'dcQty', label: 'DC Qty', align: 'right' },
                    { key: 'dcCount', label: "No. of DC's", align: 'right' },
                    { key: 'rate', label: 'Rate', align: 'right' },
                    { key: 'amount', label: 'Amount', align: 'right' },
                ],
                tableRows,
                {
                    customer: 'Total',
                    site: '',
                    grade: '',
                    dcQty: formatQty(totalDcQty),
                    dcCount: totalDcCount,
                    rate: '',
                    amount: formatAmount(totalAmount),
                },
                emptyMessage,
            ),
            width: '960px',
            confirmButtonText: 'Close',
        });
    };

    const columns = useMemo(
        () => [
            {
                fieldName: "delivery_challan_no",
                headerName: 'DC.No',
                size: 72,
                maxSize: 80,
            },
            {
                fieldName: "delivery_challan_date",
                headerName: 'DC Date',
                size: 92,
                maxSize: 100,
            },
            {
                fieldName: "delivery_challan_time",
                headerName: 'DC Time',
                size: 80,
                maxSize: 90,
            },
            {
                fieldName: 'customer_name',
                headerName: 'Customer Name',
                size: 150,
                maxSize: 170,
            },
            {
                fieldName: 'sales_rep_name',
                headerName: 'Marketting',
                size: 110,
                maxSize: 130,
            },
            {
                fieldName: "consignee_projectname",
                headerName: 'Customer Site/Project',
                size: 130,
                maxSize: 150,
            },
            {
                fieldName: 'production_plant',
                headerName: 'Production Plant',
                size: 140,
                maxSize: 160,
            },
            {
                fieldName: 'grade',
                headerName: 'Grade',
                size: 70,
                maxSize: 80,
            },
            {
                fieldName: "production_qty",
                headerName: 'Production Qty',
                size: 95,
                maxSize: 110,
                align: 'right',
            },
            {
                fieldName: "purchase_qty",
                headerName: 'Purchase Qty',
                size: 90,
                maxSize: 105,
                align: 'right',
            },
            {
                fieldName: "received_qty",
                headerName: 'Received Qty',
                size: 90,
                maxSize: 105,
                align: 'right',
            },
            {
                fieldName: "other_qty",
                headerName: 'Other Qty',
                size: 80,
                maxSize: 95,
                align: 'right',
            },
            {
                fieldName: "client_adjust_qty",
                headerName: 'Client Qty',
                size: 80,
                maxSize: 95,
                align: 'right',
            },
            {
                fieldName: "dc_qty",
                headerName: 'DC Qty',
                size: 75,
                maxSize: 90,
                align: 'right',
            },
            {
                fieldName: "invoice_qty",
                headerName: 'Invoice Qty',
                size: 85,
                maxSize: 100,
                align: 'right',
            },
            {
                fieldName: "customer_vehicle_no",
                headerName: 'Vehicle No',
                size: 110,
                maxSize: 130,
            },
            {
                fieldName: "delivery_mode",
                headerName: 'Mode of Delivery',
                size: 100,
                maxSize: 120,
            },
            {
                fieldName: "document_type",
                headerName: 'Document Type',
                size: 110,
                maxSize: 130,
            },
            {
                fieldName: "eway_bill_no",
                headerName: 'E-Way Bill No',
                size: 110,
                maxSize: 130,
            },
            {
                fieldName: "einvoice_irn",
                headerName: 'IRN',
                size: 220,
                maxSize: 260,
            },
            {
                fieldName: "einvoice_ack",
                headerName: 'ACK No',
                size: 110,
                maxSize: 130,
            },
            {
                fieldName: "user_remarks",
                headerName: 'Remarks',
                size: 140,
                maxSize: 220,
            },
            {
                fieldName: "status",
                headerName: 'DC Status',
                size: 90,
                maxSize: 110,
            },
            {
                fieldName: "dc_invioce",
                headerName: 'Invoice No',
                size: 100,
                maxSize: 120,
            },
            {
                fieldName: "id",
                headerName: "ROW_ACTIONS"
            }
        ],
        [],);

const onEditDeliveryChallan = (deliveryId, event) => {
    navigate(`/DeliveryChallanEdit/${deliveryId}/`);
};

const onDeleteDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    Swal.fire({title: 'Are you sure to Delete?',  
    showCancelButton: true,  
    confirmButtonText: `Yes`,  
    cancelButtonText: `No`,
    }).then((result) => {  
        if (result.isConfirmed) { 

        setIsLoading(true);
        deleteDeliveryChallan(cookies, deliveryId)
        .then(response => {
            console.log(`Delivery with id ${deliveryId} deleted`);
            Swal.fire('Deleted Successfully!', '', 'success');
            setRefreshKey(oldKey => oldKey +1)
        })
        .catch(error => {
            setRefreshKey(oldKey => oldKey +1);
            console.log(error.response.data);
            displayError(error.response.data,'Delete Failed');
        });  
        } else if (result.isDismissed) {    
        Swal.fire('Not Deleted', '', 'info')  
    }
    });
	  
};

const exportableColumns = useMemo(
    () => columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
    [columns],
);

const handleDownloadCsv = () => {
    if (!listViewConfig) return;
    const params = buildDeliveryChallanQueryParams(listViewConfig);
    getDcListCSV(cookies, params)
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'delivery_challan_list.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        })
        .catch((error) => {
            console.log(error.response?.data);
            displayError(error.response?.data);
        });
};

const handleExcelExport = () => {
    exportAsExcel(exportableColumns, data.records, 'delivery_challan_list');
};

const getPrintErrorMessage = async (error) => {
    const errorData = error.response?.data;
    if (errorData instanceof Blob) {
        try {
            const text = await errorData.text();
            const parsed = JSON.parse(text);
            return parsed.detail ?? text;
        } catch {
            return error.message;
        }
    }
    if (typeof errorData === 'object' && errorData !== null) {
        return errorData.detail ?? errorData.message ?? error.message;
    }
    return errorData ?? error.message;
};

const onPrintBr = (deliveryId, event) => {
    event.preventDefault();
    setIsLoading(true);
    printBatchReport(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, {
            title: 'Batch Report',
            downloadFileName: 'batch_report.pdf',
        });
        setIsLoading(false);
    })
    .catch(async (error) => {
        console.log(error);
        const errorMessage = await getPrintErrorMessage(error);
        displayError(errorMessage, 'BR print Failed');
        setIsLoading(false);
    });
};

const onPrintDeliveryChallan = (deliveryId, event) => {
    event.preventDefault();
    setIsLoading(true);
    printDeliveryChallan(cookies, deliveryId)
    .then(response => {
        openPdfResponse(response, {
            title: 'Delivery Challan',
            downloadFileName: 'delivery_challan.pdf',
        });
        setIsLoading(false);
    })
    .catch(async (error) => {
        console.log(error);
        const errorMessage = await getPrintErrorMessage(error);
        displayError(errorMessage, 'Print Failed');
        setIsLoading(false);
    });
};

const onPrintInvoice = (invoiceId, event) => {
    event.preventDefault();
    if (!invoiceId) {
        Swal.fire('Invoice is not generated for this Delivery Challan.', '', 'info');
        return;
    }
    setIsLoading(true);
    printSalesInvoice(cookies, invoiceId)
    .then(response => {
        openPdfResponse(response, {
            title: 'Sales Invoice',
            downloadFileName: 'sales_invoice.pdf',
        });
    })
    .catch(async (error) => {
        const errorMessage = await getPrintErrorMessage(error);
        displayError(errorMessage, 'Invoice Print Failed');
    })
    .finally(() => {
        setIsLoading(false);
    });
};

const handleCSVExport = () => {
    // Prefer filtered server CSV when list settings are available.
    if (listViewConfig) {
        handleDownloadCsv();
        return;
    }
    exportAsCSV(exportableColumns, data.records);
};

const handlePDFExport = () => {
    exportAsPDF(
        'List of Delivery Challan',
        exportableColumns.map((col) => ({
            header: col.headerName,
            dataKey: col.fieldName,
            align: col.align,
        })),
        data.records,
        'DeliveryChallanList.pdf',
    );
};

return (

    <div id="outer-container"  className="App" > 
        <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
        <div id="page-wrap">
        <ListFormPageHeader
            module="DISPATCH"
            title="List of Delivery Challan"
            viewKey="delivery_challan"
            onSettingsSaved={handleSettingsSaved}
            formLink="/DeliveryChallan"
            addNewLabel="New Delivery Challan"
            headerActions={
                <ListViewSettings
                    viewKey="delivery_challan"
                    onSaved={handleSettingsSaved}
                />
            }
        />

        <ListPageGradientSummary
            highlightCard={highlightCard}
            cards={summaryCards}
            onHighlightClick={handleTodayDcSummaryClick}
            onCardClick={handleSummaryCardClick}
        />

            <div className="list-form-table-wrap">
            <div className="item-list-table-container">
                <div className="table-responsive">
                <ReactTableWrapper
                            title='List of Delivery Challan'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditDeliveryChallan}
                            onRowDelete={onDeleteDeliveryChallan}
                            onCSVExport={handleCSVExport}
                            onExcelExport={handleExcelExport}
                            onPDFExport={handlePDFExport}
                            exportFilename="delivery_challan_list"
                            isLoading={isLoading}
                            hideDelete={true}
                            onRowBrPrint={onPrintBr}
                            onRowPrint={onPrintDeliveryChallan}
                            onRowInvoicePrint={onPrintInvoice}
                            hidePrint ={false}
                            left_pinned={left_pinned}
                            right_pinned={right_pinned}
                            compactColumns={true}
                        />
                </div>
            </div>
            </div> 
        </div>
        <button style={{position:"relative",right:"40px"}}className="btn btn-fill btn-primary float-end" type="button" onClick={() =>{navigate("/DeliveryChallan")}}>Back</button>
    </div>
);

}



export default DeliveryChallanTable;